import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useProject } from "../contexts/ProjectContext";
import { useAuth } from "../contexts/AuthContext";
import { useUsage } from "../hooks/useUsage";
import SubscriptionModal from "./SubscriptionModal";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { ArrowLeft, Save, Sparkles, Copy, RotateCcw, Loader2, Check, Undo2, FileText, Eye, Edit3, Plus, Bookmark } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';

const PromptEditor = () => {
  const { projectId, promptId } = useParams();
  const navigate = useNavigate();
  const { projects, prompts, systemPrompts, settings, footnotes, createPrompt, updatePrompt, improvePrompt } = useProject();
  const { user } = useAuth();
  const { usage, refreshUsage } = useUsage();
  const { toast } = useToast();

  const [prompt, setPrompt] = useState({ title: "", content: "" });
  const [originalPrompt, setOriginalPrompt] = useState({ title: "", content: "" });
  const [selectedSystemPrompt, setSelectedSystemPrompt] = useState("");
  const [isImproving, setIsImproving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [saveButtonState, setSaveButtonState] = useState("idle"); // idle, saving, saved
  const [preImprovementPrompt, setPreImprovementPrompt] = useState(null); // Store original content before improvement
  const [showUndoImprovement, setShowUndoImprovement] = useState(false);
  const [markdownMode, setMarkdownMode] = useState(false); // Toggle between markdown and plain text
  const [editorView, setEditorView] = useState("edit"); // edit, preview, split
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

  const project = projects.find(p => p.id === projectId);
  const existingPrompt = promptId ? prompts[projectId]?.find(p => p.id === promptId) : null;
  const isEditing = !!promptId;

  useEffect(() => {
    if (existingPrompt) {
      setPrompt({ title: existingPrompt.title, content: existingPrompt.content });
      setOriginalPrompt({ title: existingPrompt.title, content: existingPrompt.content });
    }
  }, [existingPrompt]);

  useEffect(() => {
    setSelectedSystemPrompt(settings.default_system_prompt || systemPrompts.find(sp => sp.is_default)?.id || '');
  }, [settings.default_system_prompt, systemPrompts]);

  useEffect(() => {
    const hasChanges = prompt.title !== originalPrompt.title || prompt.content !== originalPrompt.content;
    setHasUnsavedChanges(hasChanges);
    
    // Reset button state when user starts making changes after a successful save
    if (hasChanges && saveButtonState === "saved") {
      setSaveButtonState("idle");
    }
    
    // Hide undo button if user manually modifies the content after improvement
    if (showUndoImprovement && hasChanges && preImprovementPrompt) {
      // Check if current content is different from the improved content
      // If user is manually editing, hide the undo option
      const isManuallyEditing = prompt.content !== preImprovementPrompt.improvedContent;
      if (isManuallyEditing) {
        setShowUndoImprovement(false);
        setPreImprovementPrompt(null);
      }
    }
  }, [prompt, originalPrompt, saveButtonState, showUndoImprovement, preImprovementPrompt]);

  if (!project) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Project not found</h2>
          <Link to="/dashboard">
            <Button>Return to Dashboard</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Auto-generate title from content
  const generateTitle = (content) => {
    if (!content.trim()) return "Untitled Prompt";
    
    // Remove common prompt starters and get meaningful part
    const cleanContent = content
      .replace(/^(please|can you|write|create|generate|help me|i need|make|build)\s+/i, '')
      .trim();
    
    // Take first meaningful sentence or phrase, max 50 chars
    const firstSentence = cleanContent.split(/[.!?]/)[0];
    const title = firstSentence.length > 50 
      ? firstSentence.substring(0, 47) + '...' 
      : firstSentence;
    
    // Capitalize first letter
    return title.charAt(0).toUpperCase() + title.slice(1);
  };

  const handleUndoImprovement = () => {
    if (preImprovementPrompt) {
      setPrompt({
        title: preImprovementPrompt.title,
        content: preImprovementPrompt.content
      });
      setShowUndoImprovement(false);
      setPreImprovementPrompt(null);
      
      toast({
        title: "Undone",
        description: "Prompt improvement has been undone"
      });
    }
  };

  const handleSave = async () => {
    if (!prompt.content.trim()) {
      toast({
        title: "Error",
        description: "Prompt content is required",
        variant: "destructive"
      });
      return;
    }

    // Prevent multiple simultaneous save attempts
    if (saveButtonState === "saving") {
      return;
    }

    setSaveButtonState("saving");
    try {
      // Auto-generate title if empty
      const finalPrompt = {
        ...prompt,
        title: prompt.title.trim() || generateTitle(prompt.content)
      };

      if (isEditing) {
        await updatePrompt(projectId, promptId, finalPrompt);
        setOriginalPrompt({ ...finalPrompt });
        setSaveButtonState("saved");
        
        // Hide undo option after saving since changes are now committed
        setShowUndoImprovement(false);
        setPreImprovementPrompt(null);
        
        // Reset button state after showing success
        setTimeout(() => {
          setSaveButtonState("idle");
        }, 2000);
        
        toast({
          title: "Success",
          description: "Prompt updated successfully"
        });
      } else {
        const newPrompt = await createPrompt(projectId, finalPrompt);
        setSaveButtonState("saved");
        
        // Brief success state before navigation
        setTimeout(() => {
          navigate(`/project/${projectId}/prompt/${newPrompt.id}`);
        }, 1000);
        
        toast({
          title: "Success",
          description: "Prompt created successfully"
        });
      }
    } catch (error) {
      console.error('Save error details:', error);
      setSaveButtonState("idle");
      
      // More specific error handling
      let errorMessage = isEditing ? "Failed to update prompt" : "Failed to create prompt";
      
      if (error.response?.status === 400) {
        errorMessage = error.response.data?.detail || errorMessage;
      } else if (error.response?.status >= 500) {
        errorMessage = "Server error. Please try again.";
      } else if (!navigator.onLine) {
        errorMessage = "No internet connection. Please check your network.";
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    }
  };

  const handleImprove = async () => {
    if (!prompt.content.trim()) {
      toast({
        title: "Error",
        description: "Please enter some content to improve",
        variant: "destructive"
      });
      return;
    }

    // Check if user can use AI improvement
    if (!usage.can_use_ai_improvement) {
      setShowSubscriptionModal(true);
      return;
    }

    // Store current prompt content before improvement
    setPreImprovementPrompt({
      title: prompt.title,
      content: prompt.content,
      improvedContent: null // Will be set after improvement
    });

    setIsImproving(true);
    try {
      const improvedContent = await improvePrompt(prompt.content, selectedSystemPrompt);
      
      // Update the stored pre-improvement data with the improved content
      setPreImprovementPrompt(prev => ({
        ...prev,
        improvedContent: improvedContent
      }));
      
      setPrompt({ ...prompt, content: improvedContent });
      setShowUndoImprovement(true);
      
      // Refresh usage after successful improvement
      refreshUsage();
      
      toast({
        title: "Success",
        description: "Prompt improved successfully"
      });
    } catch (error) {
      // Reset pre-improvement data if improvement fails
      setPreImprovementPrompt(null);
      
      // Handle specific error cases
      if (error.response?.status === 402) {
        const errorDetail = error.response.data?.detail;
        if (typeof errorDetail === 'object' && errorDetail.subscription_required) {
          setShowSubscriptionModal(true);
        } else {
          toast({
            title: "Usage Limit Reached",
            description: errorDetail?.message || errorDetail || "You've reached your AI improvement limit. Upgrade to continue.",
            variant: "destructive",
          });
        }
      } else {
        toast({
          title: "Error",
          description: "Failed to improve prompt. Please try again.",
          variant: "destructive"
        });
      }
    } finally {
      setIsImproving(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(prompt.content);
      setIsCopied(true);
      toast({
        title: "Copied",
        description: "Prompt copied to clipboard"
      });
      
      // Reset icon after 2 seconds
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy to clipboard",
        variant: "destructive"
      });
    }
  };

  const handleReset = () => {
    if (hasUnsavedChanges && window.confirm("Are you sure you want to discard your changes?")) {
      setPrompt({ ...originalPrompt });
    }
  };

  const handleAppendFootnote = (footnoteId) => {
    const footnote = footnotes.find(f => f.id === footnoteId);
    if (footnote) {
      setPrompt(prev => ({
        ...prev,
        content: prev.content + "\n\n" + footnote.content
      }));
      
      toast({
        title: "Footnote added",
        description: `"${footnote.name}" has been appended to your prompt`
      });
    }
  };

  const wordCount = prompt.content.trim().split(/\s+/).filter(word => word.length > 0).length;
  const charCount = prompt.content.length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to={`/project/${projectId}`}>
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Project
                </Button>
              </Link>
              <div>
                <h1 className="text-xl font-bold text-slate-800">
                  {isEditing ? "Edit Prompt" : "New Prompt"}
                </h1>
                <p className="text-sm text-slate-500">{project.name}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {hasUnsavedChanges && (
                <Button variant="outline" size="sm" onClick={handleReset}>
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset
                </Button>
              )}
              <Button 
                onClick={handleSave} 
                disabled={saveButtonState !== "idle"}
                className={saveButtonState === "saved" ? "bg-green-600 hover:bg-green-700 text-white" : "bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white"}
              >
                <Save className="w-4 h-4 mr-2" />
                {saveButtonState === "saving" && (isEditing ? "Updating..." : "Creating...")}
                {saveButtonState === "saved" && (isEditing ? "Updated!" : "Created!")}
                {saveButtonState === "idle" && (isEditing ? "Update" : "Create")}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Editor */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Prompt Details</CardTitle>
                <CardDescription>
                  Create or edit your prompt content and settings
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="title">Title (Optional)</Label>
                  <Input
                    id="title"
                    placeholder="Leave empty to auto-generate from content..."
                    value={prompt.title}
                    onChange={(e) => setPrompt({ ...prompt, title: e.target.value })}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    If left empty, a title will be automatically generated when you save
                  </p>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label htmlFor="content">Content *</Label>
                    <div className="flex items-center space-x-2">
                      <Button
                        type="button"
                        variant={markdownMode ? "default" : "outline"}
                        size="sm"
                        onClick={() => setMarkdownMode(!markdownMode)}
                        className="text-xs"
                      >
                        <FileText className="w-3 h-3 mr-1" />
                        {markdownMode ? "Markdown" : "Plain Text"}
                      </Button>
                      {markdownMode && (
                        <div className="flex rounded-md overflow-hidden border">
                          <Button
                            type="button"
                            variant={editorView === "edit" ? "default" : "ghost"}
                            size="sm"
                            onClick={() => setEditorView("edit")}
                            className="text-xs rounded-none"
                          >
                            <Edit3 className="w-3 h-3 mr-1" />
                            Edit
                          </Button>
                          <Button
                            type="button"
                            variant={editorView === "preview" ? "default" : "ghost"}
                            size="sm"
                            onClick={() => setEditorView("preview")}
                            className="text-xs rounded-none"
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            Preview
                          </Button>
                          <Button
                            type="button"
                            variant={editorView === "split" ? "default" : "ghost"}
                            size="sm"
                            onClick={() => setEditorView("split")}
                            className="text-xs rounded-none"
                          >
                            Split
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {!markdownMode ? (
                    // Plain text editor
                    <Textarea
                      id="content"
                      placeholder="Write your prompt here..."
                      rows={12}
                      value={prompt.content}
                      onChange={(e) => setPrompt({ ...prompt, content: e.target.value })}
                      className="resize-none"
                    />
                  ) : (
                    // Markdown editor with different views
                    <div className="border rounded-md overflow-hidden">
                      {editorView === "edit" && (
                        <Textarea
                          id="content"
                          placeholder="Write your prompt in markdown...

Examples:
# Heading 1
## Heading 2
**Bold text**
*Italic text*
- Bullet point
1. Numbered list
`inline code`
```
code block
```"
                          rows={12}
                          value={prompt.content}
                          onChange={(e) => setPrompt({ ...prompt, content: e.target.value })}
                          className="resize-none border-0 rounded-none"
                        />
                      )}
                      
                      {editorView === "preview" && (
                        <div className="p-4 bg-white border-0 min-h-[300px] prose prose-sm max-w-none">
                          {prompt.content.trim() ? (
                            <ReactMarkdown 
                              remarkPlugins={[remarkGfm]}
                              rehypePlugins={[rehypeRaw, rehypeSanitize]}
                              components={{
                                h1: ({node, ...props}) => <h1 className="text-2xl font-bold text-slate-800 mb-4" {...props} />,
                                h2: ({node, ...props}) => <h2 className="text-xl font-semibold text-slate-700 mb-3" {...props} />,
                                h3: ({node, ...props}) => <h3 className="text-lg font-medium text-slate-700 mb-2" {...props} />,
                                p: ({node, ...props}) => <p className="text-slate-600 mb-3 leading-relaxed" {...props} />,
                                ul: ({node, ...props}) => <ul className="list-disc list-inside text-slate-600 mb-3 space-y-1" {...props} />,
                                ol: ({node, ...props}) => <ol className="list-decimal list-inside text-slate-600 mb-3 space-y-1" {...props} />,
                                li: ({node, ...props}) => <li className="text-slate-600" {...props} />,
                                strong: ({node, ...props}) => <strong className="font-semibold text-slate-800" {...props} />,
                                em: ({node, ...props}) => <em className="italic text-slate-700" {...props} />,
                                code: ({node, ...props}) => <code className="bg-slate-100 text-slate-800 px-1 py-0.5 rounded text-sm font-mono" {...props} />,
                                pre: ({node, ...props}) => <pre className="bg-slate-100 text-slate-800 p-3 rounded-md overflow-x-auto mb-3 text-sm font-mono" {...props} />,
                                blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-slate-300 pl-4 text-slate-600 italic mb-3" {...props} />,
                                hr: ({node, ...props}) => <hr className="border-slate-200 my-6" {...props} />,
                                table: ({node, ...props}) => <table className="min-w-full border-collapse border border-slate-300 mb-3" {...props} />,
                                th: ({node, ...props}) => <th className="border border-slate-300 px-2 py-1 bg-slate-50 font-semibold text-slate-700" {...props} />,
                                td: ({node, ...props}) => <td className="border border-slate-300 px-2 py-1 text-slate-600" {...props} />
                              }}
                            >
                              {prompt.content}
                            </ReactMarkdown>
                          ) : (
                            <p className="text-slate-400 italic">Nothing to preview yet. Start typing in markdown...</p>
                          )}
                        </div>
                      )}
                      
                      {editorView === "split" && (
                        <div className="grid grid-cols-2 min-h-[300px]">
                          <div className="border-r">
                            <Textarea
                              id="content"
                              placeholder="Write your prompt in markdown..."
                              rows={12}
                              value={prompt.content}
                              onChange={(e) => setPrompt({ ...prompt, content: e.target.value })}
                              className="resize-none border-0 rounded-none h-full"
                            />
                          </div>
                          <div className="p-4 bg-white overflow-y-auto prose prose-sm max-w-none">
                            {prompt.content.trim() ? (
                              <ReactMarkdown 
                                remarkPlugins={[remarkGfm]}
                                rehypePlugins={[rehypeRaw, rehypeSanitize]}
                                components={{
                                  h1: ({node, ...props}) => <h1 className="text-xl font-bold text-slate-800 mb-3" {...props} />,
                                  h2: ({node, ...props}) => <h2 className="text-lg font-semibold text-slate-700 mb-2" {...props} />,
                                  h3: ({node, ...props}) => <h3 className="text-base font-medium text-slate-700 mb-2" {...props} />,
                                  p: ({node, ...props}) => <p className="text-slate-600 mb-2 leading-relaxed text-sm" {...props} />,
                                  ul: ({node, ...props}) => <ul className="list-disc list-inside text-slate-600 mb-2 space-y-1 text-sm" {...props} />,
                                  ol: ({node, ...props}) => <ol className="list-decimal list-inside text-slate-600 mb-2 space-y-1 text-sm" {...props} />,
                                  li: ({node, ...props}) => <li className="text-slate-600 text-sm" {...props} />,
                                  strong: ({node, ...props}) => <strong className="font-semibold text-slate-800" {...props} />,
                                  em: ({node, ...props}) => <em className="italic text-slate-700" {...props} />,
                                  code: ({node, ...props}) => <code className="bg-slate-100 text-slate-800 px-1 py-0.5 rounded text-xs font-mono" {...props} />,
                                  pre: ({node, ...props}) => <pre className="bg-slate-100 text-slate-800 p-2 rounded-md overflow-x-auto mb-2 text-xs font-mono" {...props} />,
                                  blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-slate-300 pl-3 text-slate-600 italic mb-2 text-sm" {...props} />,
                                  hr: ({node, ...props}) => <hr className="border-slate-200 my-4" {...props} />,
                                  table: ({node, ...props}) => <table className="min-w-full border-collapse border border-slate-300 mb-2 text-xs" {...props} />,
                                  th: ({node, ...props}) => <th className="border border-slate-300 px-1 py-1 bg-slate-50 font-semibold text-slate-700 text-xs" {...props} />,
                                  td: ({node, ...props}) => <td className="border border-slate-300 px-1 py-1 text-slate-600 text-xs" {...props} />
                                }}
                              >
                                {prompt.content}
                              </ReactMarkdown>
                            ) : (
                              <p className="text-slate-400 italic text-sm">Live preview...</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="flex justify-between text-xs text-slate-500 mt-2">
                    <span>{wordCount} words, {charCount} characters</span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={handleCopy}
                      disabled={!prompt.content.trim()}
                      className={isCopied ? "text-green-600" : ""}
                    >
                      {isCopied ? (
                        <>
                          <Check className="w-3 h-3 mr-1" />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy className="w-3 h-3 mr-1" />
                          Copy
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {markdownMode && (
                    <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <h4 className="font-medium text-blue-800 mb-2 text-sm">📝 Markdown Quick Guide</h4>
                      <div className="text-xs text-blue-700 space-y-1">
                        <p><code className="bg-blue-100 px-1 rounded"># Heading</code> → <strong>Large heading</strong></p>
                        <p><code className="bg-blue-100 px-1 rounded">**Bold**</code> → <strong>Bold text</strong></p>
                        <p><code className="bg-blue-100 px-1 rounded">*Italic*</code> → <em>Italic text</em></p>
                        <p><code className="bg-blue-100 px-1 rounded">- Item</code> → • Bullet point</p>
                        <p><code className="bg-blue-100 px-1 rounded">`code`</code> → <code className="bg-slate-100 px-1 rounded">inline code</code></p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* AI Improvement */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Sparkles className="w-5 h-5 mr-2 text-violet-500" />
                  AI Improvement
                </CardTitle>
                <CardDescription>
                  Use AI to enhance your prompt
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="system-prompt">System Prompt</Label>
                  <Select value={selectedSystemPrompt} onValueChange={setSelectedSystemPrompt}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select system prompt" />
                    </SelectTrigger>
                    <SelectContent>
                      {systemPrompts.map((sp) => (
                        <SelectItem key={sp.id} value={sp.id}>
                          {sp.name} {sp.is_default && "(Default)"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  onClick={handleImprove} 
                  disabled={isImproving || !prompt.content.trim()}
                  className="w-full bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white"
                >
                  {isImproving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Improving...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Improve Prompt
                    </>
                  )}
                </Button>
                
                {showUndoImprovement && (
                  <Button 
                    onClick={handleUndoImprovement}
                    variant="outline"
                    className="w-full mt-2 text-amber-700 border-amber-300 hover:bg-amber-50"
                  >
                    <Undo2 className="w-4 h-4 mr-2" />
                    Undo Improvement
                  </Button>
                )}
                
                {/* Usage information for AI improvements */}
                <div className="text-xs text-slate-600 bg-slate-50 p-2 rounded">
                  💡 AI Improvements: {usage.ai_improvements_used}/
                  {usage.subscription_status === 'free' ? '200' : '∞'} used
                  {usage.subscription_status === 'free' && (
                    <span className="text-slate-500"> (Free plan)</span>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Footnotes */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Bookmark className="w-5 h-5 mr-2 text-blue-500" />
                  Quick Footnotes
                </CardTitle>
                <CardDescription>
                  Append saved text snippets to your prompt
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {footnotes.length > 0 ? (
                  <>
                    <div className="space-y-2">
                      {footnotes.map((footnote) => (
                        <Button
                          key={footnote.id}
                          variant="outline"
                          size="sm"
                          onClick={() => handleAppendFootnote(footnote.id)}
                          className="w-full justify-start text-left h-auto py-2 px-3"
                        >
                          <Plus className="w-3 h-3 mr-2 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-xs text-slate-700 truncate">
                              {footnote.name}
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                              {footnote.content.trim().substring(0, 60)}...
                            </div>
                          </div>
                        </Button>
                      ))}
                    </div>
                    <div className="text-xs text-slate-500 bg-slate-50 p-2 rounded">
                      💡 Click any footnote to append it to your prompt content
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-slate-500 text-center py-4">
                    No footnotes created yet. You can create them in Settings.
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Tips */}
            <Card>
              <CardHeader>
                <CardTitle>
                  {markdownMode ? "Markdown Tips" : "Tips for Better Prompts"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {markdownMode ? (
                  <div className="text-sm text-slate-600 space-y-3">
                    <div>
                      <p className="font-medium text-slate-700 mb-1">Structure your prompts:</p>
                      <ul className="space-y-1 text-xs">
                        <li>• Use <code className="bg-slate-100 px-1 rounded"># Headings</code> for sections</li>
                        <li>• Use <code className="bg-slate-100 px-1 rounded">- Lists</code> for requirements</li>
                        <li>• Use <code className="bg-slate-100 px-1 rounded">**Bold**</code> for emphasis</li>
                        <li>• Use <code className="bg-slate-100 px-1 rounded">`code`</code> for examples</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-medium text-slate-700 mb-1">Advanced formatting:</p>
                      <ul className="space-y-1 text-xs">
                        <li>• Tables with <code className="bg-slate-100 px-1 rounded">| Column |</code></li>
                        <li>• Code blocks with <code className="bg-slate-100 px-1 rounded">```</code></li>
                        <li>• Quotes with <code className="bg-slate-100 px-1 rounded">&gt; Text</code></li>
                        <li>• Links with <code className="bg-slate-100 px-1 rounded">[text](url)</code></li>
                      </ul>
                    </div>
                  </div>
                ) : (
                  <ul className="text-sm text-slate-600 space-y-2">
                    <li>• Be specific about what you want</li>
                    <li>• Include context and examples</li>
                    <li>• Specify the desired format</li>
                    <li>• Define the target audience</li>
                    <li>• Use clear, actionable language</li>
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        currentUsage={usage}
      />
    </div>
  );
};

export default PromptEditor;